import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  handle: string;
  picture?: string;
  headline: string;
  bio: string;
  bannerImage: string;
  isProfileCompleted: boolean;
  creationDate: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => void;  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<boolean>;
  completeProfileSetup: (profileData: { name: string; handle?: string; headline?: string; bio?: string; isSkipping?: boolean }) => Promise<boolean>;  checkHandleAvailability: (handle: string) => Promise<{ available: boolean; suggestions?: string[]; error?: string }> ;
  getUserByHandle: (handle: string, forceRefresh?: boolean) => Promise<User | null>;
  clearUserCache: (handle: string) => void;
  followUser: (targetHandle: string) => Promise<boolean>;
  unfollowUser: (targetHandle: string) => Promise<boolean>;
  checkFollowStatus: (targetHandle: string) => Promise<{ isFollowing: boolean; followerCount: number; followingCount: number; isOwnProfile: boolean } | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const userCacheRef = useRef<Map<string, User>>(new Map()); // Use ref for cache to avoid dependency issues
  
  const refreshSession = async () => {
    try {
      // Check for session token in URL parameters (from OAuth redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const sessionFromUrl = urlParams.get('session');
      
      if (sessionFromUrl) {
        // Store session token in localStorage
        localStorage.setItem('session_token', sessionFromUrl);
        // Clean up URL by removing the session parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Get session token from localStorage
      const sessionToken = localStorage.getItem('session_token');
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Add Authorization header if we have a session token
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/session`, {
        credentials: 'include',
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        // console.log('Session data:', data); // Debug log
        setUser(data.user);
      } else {
        console.log('Session response not ok:', response.status); // Debug log
        // Clear invalid session token
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };const signIn = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/signin`;
  };  const signOut = async () => {
    try {
      // Get session token from localStorage
      const sessionToken = localStorage.getItem('session_token');
      
      if (sessionToken) {
        // Make API call to signout with Bearer token
        await fetch(`${process.env.REACT_APP_API_URL}/auth/signout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Error during signout:', error);
      // Continue with logout even if server call fails
    }
    
    // Always clear local session and redirect
    localStorage.removeItem('session_token');
    setUser(null);
    
    // Redirect to feed page
    window.location.href = '/';
  };

  const updateProfile = async (profileData: Partial<User>): Promise<boolean> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      if (!sessionToken) {
        console.error('No session token found');
        return false;
      }      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        
        // Clear cache for this user's handle so fresh data is fetched next time
        if (updatedUser.handle) {
          userCacheRef.current.delete(updatedUser.handle);
          console.log('Cleared cache for handle:', updatedUser.handle);
        }
        
        return true;
      } else {
        console.error('Failed to update profile:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  };

  const completeProfileSetup = async (profileData: { name: string; handle?: string; headline?: string; bio?: string; isSkipping?: boolean }): Promise<boolean> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      if (!sessionToken) {
        console.error('No session token found');
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        return true;
      } else {
        console.error('Failed to complete profile setup:', response.status);
        return false;
      }
    } catch (error) {
      console.error('Error completing profile setup:', error);
      return false;
    }
  };

  // Handle-related functions
  const checkHandleAvailability = async (handle: string): Promise<{ available: boolean; suggestions?: string[]; error?: string }> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/handle/${handle}/availability`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json();
        return { available: false, error: errorData.error };
      }
    } catch (error) {
      console.error('Error checking handle availability:', error);
      return { available: false, error: 'Failed to check handle availability' };
    }
  };  const getUserByHandle = useCallback(async (handle: string, forceRefresh: boolean = false): Promise<User | null> => {
    // Check cache first (unless force refresh is requested)
    if (!forceRefresh && userCacheRef.current.has(handle)) {
      console.log('Cache hit for handle:', handle);
      return userCacheRef.current.get(handle) || null;
    }
    
    console.log('Making API call for handle:', handle, forceRefresh ? '(force refresh)' : '');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/@${handle}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // Cache the result
        userCacheRef.current.set(handle, userData);
        return userData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting user by handle:', error);
      return null;
    }
  }, []);// No dependencies needed since we're using ref
  const clearUserCache = useCallback((handle: string) => {
    console.log('Clearing cache for handle:', handle);
    userCacheRef.current.delete(handle);  }, []);

  // Follow/unfollow functions
  const followUser = useCallback(async (targetHandle: string): Promise<boolean> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        console.error('No session token found');
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/follow/${targetHandle}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log(`Successfully followed @${targetHandle}`);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to follow user:', errorData.error);
        return false;
      }
    } catch (error) {
      console.error('Error following user:', error);
      return false;
    }
  }, []);

  const unfollowUser = useCallback(async (targetHandle: string): Promise<boolean> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        console.error('No session token found');
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/follow/${targetHandle}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log(`Successfully unfollowed @${targetHandle}`);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Failed to unfollow user:', errorData.error);
        return false;
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return false;
    }
  }, []);

  const checkFollowStatus = useCallback(async (targetHandle: string): Promise<{ isFollowing: boolean; followerCount: number; followingCount: number; isOwnProfile: boolean } | null> => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        console.error('No session token found');
        return null;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/follow-status/${targetHandle}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          isFollowing: data.isFollowing,
          followerCount: data.followerCount,
          followingCount: data.followingCount,
          isOwnProfile: data.isOwnProfile
        };
      } else {
        const errorData = await response.json();
        console.error('Failed to check follow status:', errorData.error);
        return null;
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, []); 
  const value = {
    user,
    loading,
    signIn,
    signOut,
    refreshSession,
    updateProfile,
    completeProfileSetup,    checkHandleAvailability,
    getUserByHandle,
    clearUserCache,
    followUser,
    unfollowUser,
    checkFollowStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
